<div class="flex h-full min-h-0 w-full flex-col overflow-hidden bg-white dark:bg-zinc-950">
  <!-- One filter row, above everything it scopes. -->
  <div class="shrink-0 border-b border-zinc-200 p-4 dark:border-white/10">
    <div class="flex flex-wrap items-center gap-3">
      <div class="relative min-w-56 flex-1">
        <input
          type="search"
          class="w-full rounded-lg border border-zinc-200 bg-white py-2 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-violet-400 focus:outline-none dark:border-white/10 dark:bg-zinc-900 dark:text-zinc-100"
          placeholder="Search brand, agency or person"
          aria-label="Search cancellations"
          [value]="searchQuery()"
          (input)="onSearchInput($event)"
        />
        <svg
          class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400"
          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round" aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
      </div>

      <button
        type="button"
        class="rounded-lg border px-3 py-1.5 text-xs font-medium"
        [class]="getFilterChipClass(commentsOnly())"
        [attr.aria-pressed]="commentsOnly()"
        (click)="toggleCommentsOnly()"
      >
        Left a comment
      </button>

      @if (selectedReturn(); as returnFilter) {
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-lg border border-violet-500 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700 dark:border-violet-400 dark:bg-violet-500/20 dark:text-violet-100"
        (click)="clearReturnFilter()"
      >
        <span>Would return: {{ returnFilter }}</span>
        <span aria-hidden="true">×</span>
        <span class="sr-only">Clear return likelihood filter</span>
      </button>
      }
    </div>

    <div class="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Filter by outcome">
      @for (filter of outcomeFilters; track filter.id) {
      <button
        type="button"
        class="rounded-lg border px-2.5 py-1 text-xs font-medium"
        [class]="getFilterChipClass(isOutcomeSelected(filter.id))"
        [attr.aria-pressed]="isOutcomeSelected(filter.id)"
        (click)="toggleOutcome(filter.id)"
      >
        {{ filter.label }}
      </button>
      }
      <span class="ml-auto text-xs text-zinc-500 dark:text-zinc-400">
        {{ totalCount() }} {{ totalCount() === 1 ? 'response' : 'responses' }}
      </span>
    </div>
  </div>

  @if (loadingState() === 'error' && error()) {
  <div
    class="mx-4 mt-4 shrink-0 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-500/30 dark:bg-rose-500/10"
    role="alert"
  >
    <p class="m-0 text-sm text-rose-700 dark:text-rose-300">{{ error() }}</p>
  </div>
  }

  @if (loadingState() === 'forbidden') {
  <div class="flex flex-1 items-center justify-center p-8">
    <div class="max-w-md text-center">
      <h3 class="m-0 text-lg font-semibold text-zinc-900 dark:text-white">GodMode access required</h3>
      <p class="m-0 mt-2 text-sm text-zinc-600 dark:text-zinc-400">
        Individual exit surveys name the account, quote what they wrote and carry
        their billing ids, so they are limited to GodMode admins.
      </p>
    </div>
  </div>
  } @else {
  <div
    class="min-h-0 flex-1 overflow-auto scrollbar-transparent"
    #scrollDiv
    infinite-scroll
    [infiniteScrollDistance]="scrollDistance"
    [infiniteScrollThrottle]="throttle"
    [infiniteScrollContainer]="scrollDiv"
    (scrolled)="onScrollDown()"
  >
    <div class="overflow-x-auto" [class.is-refreshing]="isRefreshing()">
      <table class="min-w-full divide-y divide-zinc-200 text-sm dark:divide-zinc-700">
        <caption class="sr-only">Individual exit-survey responses</caption>
        <thead class="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-800/80">
          <tr>
            <th scope="col" class="px-4 py-3 text-left font-semibold text-zinc-700 dark:text-zinc-300">Account</th>
            <th scope="col" class="px-4 py-3 text-left font-semibold text-zinc-700 dark:text-zinc-300">
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded hover:text-violet-700 dark:hover:text-violet-300"
                [class.text-violet-700]="isSortedBy('reasonId')"
                (click)="toggleSort('reasonId')"
              >
                Reason
                @if (isSortedBy('reasonId')) {
                <span aria-hidden="true">{{ sortDirection() === 'ASC' ? '↑' : '↓' }}</span>
                }
              </button>
            </th>
            <th scope="col" class="px-4 py-3 text-left font-semibold text-zinc-700 dark:text-zinc-300">Would return</th>
            <th scope="col" class="px-4 py-3 text-left font-semibold text-zinc-700 dark:text-zinc-300">
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded hover:text-violet-700 dark:hover:text-violet-300"
                [class.text-violet-700]="isSortedBy('outcome')"
                (click)="toggleSort('outcome')"
              >
                Outcome
                @if (isSortedBy('outcome')) {
                <span aria-hidden="true">{{ sortDirection() === 'ASC' ? '↑' : '↓' }}</span>
                }
              </button>
            </th>
            <th scope="col" class="px-4 py-3 text-right font-semibold text-zinc-700 dark:text-zinc-300">
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded hover:text-violet-700 dark:hover:text-violet-300"
                [class.text-violet-700]="isSortedBy('renewalAmountMinorUnits')"
                (click)="toggleSort('renewalAmountMinorUnits')"
              >
                Value / mo
                @if (isSortedBy('renewalAmountMinorUnits')) {
                <span aria-hidden="true">{{ sortDirection() === 'ASC' ? '↑' : '↓' }}</span>
                }
              </button>
            </th>
            <th scope="col" class="px-4 py-3 text-left font-semibold text-zinc-700 dark:text-zinc-300">Comment</th>
            <th scope="col" class="px-4 py-3 text-left font-semibold text-zinc-700 dark:text-zinc-300">
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded hover:text-violet-700 dark:hover:text-violet-300"
                [class.text-violet-700]="isSortedBy('createdDate')"
                (click)="toggleSort('createdDate')"
              >
                Date
                @if (isSortedBy('createdDate')) {
                <span aria-hidden="true">{{ sortDirection() === 'ASC' ? '↑' : '↓' }}</span>
                }
              </button>
            </th>
          </tr>
        </thead>

        <tbody class="divide-y divide-zinc-100 dark:divide-zinc-800">
          @for (row of rows(); track row.id) {
          <tr
            class="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-900/50"
            tabindex="0"
            (click)="openResponse(row.id)"
            (keydown.enter)="openResponse(row.id)"
            (keydown.space)="openResponse(row.id)"
          >
            <td class="px-4 py-3">
              <div class="flex items-center gap-3">
                <span class="size-8 shrink-0 overflow-hidden rounded-full bg-zinc-100 dark:bg-white/5">
                  <atom-ldg-img
                    inputClass="object-cover"
                    w="full"
                    h="full"
                    radius="full"
                    [src]="imageService.getImageSource(row.account.logoUrl, 'gallery')"
                  />
                </span>
                <span class="min-w-0">
                  <span class="block truncate font-medium text-zinc-900 dark:text-zinc-100">
                    {{ row.account.name ?? 'Unknown account' }}
                  </span>
                  <span class="block truncate text-xs text-zinc-500 dark:text-zinc-400">
                    {{ scopeLabel(row.scope) }}
                    @if (row.account.userName) {
                    · {{ row.account.userName }}
                    }
                  </span>
                </span>
              </div>
            </td>
            <td class="px-4 py-3 text-zinc-700 dark:text-zinc-300">
              <span class="block">{{ row.reasonLabel ?? '—' }}</span>
              @if (row.followUpLabels.length > 0) {
              <span class="block text-xs text-zinc-500 dark:text-zinc-500">
                {{ row.followUpLabels.join(', ') }}
              </span>
              }
            </td>
            <td class="px-4 py-3 text-zinc-700 dark:text-zinc-300">{{ row.returnLabel ?? '—' }}</td>
            <td class="px-4 py-3">
              <span
                class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                [class]="outcomeChipClass(row.outcome)"
              >
                {{ outcomeLabel(row.outcome) }}
              </span>
            </td>
            <td class="px-4 py-3 text-right tabular-nums text-zinc-700 dark:text-zinc-300">{{ money(row) }}</td>
            <td class="max-w-xs px-4 py-3 text-zinc-600 dark:text-zinc-400">
              @if (row.comment) {
              <span class="line-clamp-2">{{ row.comment }}</span>
              } @else {
              <span class="text-zinc-400">—</span>
              }
            </td>
            <td class="whitespace-nowrap px-4 py-3 tabular-nums text-zinc-600 dark:text-zinc-400">
              {{ row.createdDate | date: 'dd MMM yy' }}
            </td>
          </tr>
          }

          @if (loadingMore()) {
          <tr>
            <td colspan="7" class="px-4 py-3 text-center text-xs text-zinc-500">Loading more…</td>
          </tr>
          }
        </tbody>
      </table>

      @if (loadingState() === 'loading' && rows().length === 0) {
      <div class="p-8">
        <atom-loading-inline title="Loading responses" message="Reading the exit surveys…" />
      </div>
      } @else if (loadingState() === 'loaded' && rows().length === 0) {
      <div class="p-12 text-center">
        <h3 class="m-0 text-sm font-semibold text-zinc-900 dark:text-zinc-100">No responses match</h3>
        <p class="m-0 mt-1 text-sm text-zinc-500">
          Try widening the date range or clearing a filter.
        </p>
      </div>
      }
    </div>
  </div>
  }
</div>

@if (openResponseId(); as responseId) {
<admin-cancellation-detail-drawer [responseId]="responseId" (closed)="closeResponse()" />
}
